import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  CheckCircle2, XCircle, Clock, AlertCircle, 
  TrendingDown, DollarSign, Users, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WithdrawalManagementPage = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, all
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const endpoint = filter === 'pending' 
        ? `${API_URL}/api/admin/withdrawals/pending`
        : `${API_URL}/api/admin/withdrawals/all`;

      const [withdrawalsRes, statsRes] = await Promise.all([
        fetch(endpoint, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/withdrawals/stats`, { credentials: 'include' })
      ]);

      const withdrawalsData = await withdrawalsRes.json();
      const statsData = await statsRes.json();

      setWithdrawals(withdrawalsData.withdrawals || []);
      setStats(statsData.stats || {});
    } catch (error) {
      toast.error('Failed to load withdrawal data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/withdrawals/${selectedRequest.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: approvalNotes })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Approval failed');
      }

      toast.success('Withdrawal approved successfully');
      setShowApproveModal(false);
      setApprovalNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/withdrawals/${selectedRequest.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: rejectionReason })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Rejection failed');
      }

      toast.success('Withdrawal rejected');
      setShowRejectModal(false);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!infoMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/withdrawals/${selectedRequest._id}/request-info`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ message: infoMessage })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Request failed');
      }

      toast.success('Information request sent to user');
      setShowInfoModal(false);
      setInfoMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-blue-100 text-blue-800',
      pending_cooldown: 'bg-yellow-100 text-yellow-800',
      pending_approval: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      processing: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    if (status === 'rejected' || status === 'failed') return <XCircle className="h-5 w-5 text-red-600" />;
    if (['pending', 'pending_approval', 'pending_cooldown'].includes(status)) return <Clock className="h-5 w-5 text-blue-600" />;
    return <AlertCircle className="h-5 w-5 text-gray-600" />;
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Withdrawal Management</h1>
            <p className="text-muted-foreground">Review and approve withdrawal requests</p>
          </div>
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-blue-600">{stats.pending || 0}</div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-green-600">{stats.completed || 0}</div>
                <p className="text-sm text-muted-foreground">Completed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">${stats.total_withdrawn_usd?.toFixed(2) || 0}</div>
                <p className="text-sm text-muted-foreground">Total Withdrawn</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-red-600">{stats.rejected || 0}</div>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 border-b-2 font-medium ${
              filter === 'pending' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Pending ({stats?.pending || 0})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 border-b-2 font-medium ${
              filter === 'all' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            All Requests
          </button>
        </div>

        {/* Withdrawals List */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : withdrawals.length === 0 ? (
              <div className="text-center py-12">
                <TrendingDown className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No withdrawal requests found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {withdrawals.map((request) => (
                  <div key={request.id} className="border rounded-lg p-4 hover:bg-muted/50 transition">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Header Row */}
                        <div className="flex items-center gap-3 mb-3">
                          {getStatusIcon(request.status ?? 'pending')}
                          <div>
                            <div className="font-semibold">{request.user_name ?? request.user_email ?? 'Unknown'}</div>
                            <div className="text-xs text-muted-foreground">
                              {request.user_email && request.user_name && `${request.user_email} • `}
                              Tier: {request.user_tier ?? 'N/A'} • Requested: {new Date(request.created_at ?? Date.now()).toLocaleString()}
                            </div>
                          </div>
                          <Badge className={getStatusColor(request.status ?? 'pending')}>
                            {(request.status ?? 'pending').replace('_', ' ')}
                          </Badge>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-muted-foreground">Amount</div>
                            <div className="font-bold">
                              {request.amount ?? 0} {request.asset_symbol ?? request.currency ?? 'N/A'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              ≈ ${(request.amount_usd ?? request.amount ?? 0).toFixed(2)}
                            </div>
                          </div>
                          
                          <div>
                            <div className="text-muted-foreground">Net Amount</div>
                            <div className="font-medium text-green-600">
                              {(request.net_amount ?? request.amount ?? 0).toFixed(8)} {request.asset_symbol ?? request.currency ?? 'N/A'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              After fees: ${(request.net_amount_usd ?? request.amount_usd ?? request.amount ?? 0).toFixed(2)}
                            </div>
                          </div>

                          <div>
                            <div className="text-muted-foreground">Network</div>
                            <div className="font-medium">{(request.network ?? request.blockchain ?? 'N/A').toUpperCase()}</div>
                          </div>

                          <div>
                            <div className="text-muted-foreground">Fees</div>
                            <div className="text-xs">
                              Platform: {(request.fees?.platform_fee ?? request.fees?.withdrawal_fee ?? request.fee ?? 0).toFixed(8)}<br/>
                              Network: {(request.fees?.network_fee ?? 0).toFixed(8)}
                            </div>
                          </div>
                        </div>

                        {/* Payout Details */}
                        <div className="mt-3 p-3 bg-muted rounded text-sm">
                          <div className="font-semibold mb-2">Payout Details:</div>
                          
                          {/* Mobile Money Details */}
                          {request.metadata?.method === 'mobile_money' && (
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Provider:</span>
                                <span className="font-medium">{request.metadata?.mobile_money_provider_name ?? 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Account Holder:</span>
                                <span className="font-medium">{request.metadata?.mobile_money_details?.full_name ?? 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Phone Number:</span>
                                <span className="font-medium font-mono">{request.metadata?.mobile_money_details?.phone ?? 'N/A'}</span>
                              </div>
                            </div>
                          )}

                          {/* Bank Transfer Details */}
                          {request.metadata?.method === 'bank' && (
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Bank Name:</span>
                                <span className="font-medium">{request.metadata?.bank_name ?? 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Account Holder:</span>
                                <span className="font-medium">{request.metadata?.bank_account_holder ?? 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Account Number:</span>
                                <span className="font-medium font-mono">{request.metadata?.bank_account_number ?? 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Branch Code:</span>
                                <span className="font-medium font-mono">{request.metadata?.bank_branch_code ?? 'N/A'}</span>
                              </div>
                              {request.metadata?.bank_swift && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">SWIFT:</span>
                                  <span className="font-medium font-mono">{request.metadata.bank_swift}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Crypto Details */}
                          {(request.metadata?.method === 'crypto' || !request.metadata?.method) && (
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Network:</span>
                                <span className="font-medium">{request.metadata?.network ?? request.network ?? 'N/A'}</span>
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-muted-foreground">Wallet Address:</span>
                                <span className="font-medium font-mono text-xs break-all bg-background p-2 rounded border">
                                  {request.metadata?.wallet_address ?? request.destination_address ?? 'N/A'}
                                </span>
                              </div>
                              {request.metadata?.memo && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Memo/Tag:</span>
                                  <span className="font-medium font-mono">{request.metadata.memo}</span>
                                </div>
                              )}
                              {request.metadata?.destination_tag && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Destination Tag:</span>
                                  <span className="font-medium font-mono">{request.metadata.destination_tag}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Cool-down Info */}
                        {request.status === 'pending_cooldown' && request.cooldown_until && (
                          <div className="mt-3 text-sm text-yellow-700 bg-yellow-50 p-2 rounded">
                            Cool-down until: {new Date(request.cooldown_until).toLocaleString()}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      {['pending', 'pending_approval', 'pending_cooldown'].includes(request.status) && (
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowApproveModal(true);
                            }}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowRejectModal(true);
                            }}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowInfoModal(true);
                            }}
                          >
                            <AlertCircle className="h-4 w-4 mr-1" />
                            Request Info
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Approve Modal */}
        {showApproveModal && selectedRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-lg w-full mx-4">
              <CardHeader>
                <CardTitle>Approve Withdrawal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <strong>Request:</strong> {selectedRequest.amount ?? 0} {selectedRequest.asset_symbol ?? selectedRequest.currency ?? 'N/A'} to {(selectedRequest.destination_address ?? '').substring(0, 20)}...
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Approval Notes (Optional)</label>
                  <Input
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Add any notes..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowApproveModal(false);
                      setApprovalNotes('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleApprove} disabled={processing}>
                    {processing ? 'Processing...' : 'Confirm Approval'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && selectedRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-lg w-full mx-4">
              <CardHeader>
                <CardTitle>Reject Withdrawal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                  <strong>Request:</strong> {selectedRequest.amount ?? 0} {selectedRequest.asset_symbol ?? selectedRequest.currency ?? 'N/A'} to {(selectedRequest.destination_address ?? '').substring(0, 20)}...
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Rejection Reason *</label>
                  <Input
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    required
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectionReason('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleReject} 
                    disabled={processing || !rejectionReason.trim()}
                  >
                    {processing ? 'Processing...' : 'Confirm Rejection'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Request More Info Modal */}
        {showInfoModal && selectedRequest && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="max-w-lg w-full mx-4">
              <CardHeader>
                <CardTitle>Request More Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <strong>Request:</strong> {selectedRequest.amount ?? 0} {selectedRequest.asset_symbol ?? selectedRequest.currency ?? 'N/A'} to {(selectedRequest.destination_address ?? '').substring(0, 20)}...
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Message to User *</label>
                  <textarea
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                    value={infoMessage}
                    onChange={(e) => setInfoMessage(e.target.value)}
                    placeholder="What additional information do you need from the user?"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    The user will receive this message and can provide more details
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowInfoModal(false);
                      setInfoMessage('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleRequestInfo} 
                    disabled={processing || !infoMessage.trim()}
                  >
                    {processing ? 'Sending...' : 'Send Request'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
